import { useRef, useState, useCallback } from 'react';
import { captureDeploymentSummary } from './screenshotUtils';

interface UseDeployWithScreenshotOptions {
  screenshotName: string;
  screenshotVariant: 'dashboard' | 'tenant';
  onConfirm: () => void | Promise<void>;
}

export function useDeployWithScreenshot({ screenshotName, screenshotVariant, onConfirm }: UseDeployWithScreenshotOptions) {
  const summaryRef = useRef<HTMLDivElement>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployError, setDeployError] = useState<string | null>(null);

  const handleDeploy = useCallback(async () => {
    setDeployError(null);
    setIsCapturing(true);
    try {
      await captureDeploymentSummary(summaryRef, screenshotName, screenshotVariant);
    } catch {
      // Continue with deploy even if screenshot fails
    } finally {
      setIsCapturing(false);
    }

    setIsDeploying(true);
    try {
      await Promise.resolve(onConfirm());
    } catch (error) {
      setDeployError(error instanceof Error ? error.message : 'Deployment failed. Please try again.');
    } finally {
      setIsDeploying(false);
    }
  }, [screenshotName, screenshotVariant, onConfirm]);

  const primaryLabel = isCapturing ? 'Capturing...' : isDeploying ? 'Deploying...' : 'Deploy';
  const primaryDisabled = isCapturing || isDeploying;

  return { summaryRef, handleDeploy, isCapturing, isDeploying, deployError, primaryLabel, primaryDisabled };
}
